/**
 * "Which card should this go on?" — the purchase planner (§11.3).
 *
 * Opened from the sidebar, the dashboard, or the ⌘K palette (§11.3). Mounted
 * once, by `CommandProvider` in lib/commands.tsx.
 *
 * Every card is ranked by the cashback this purchase would earn on it, with
 * the reasoning shown inline, because a ranking you can't interrogate is one
 * you have to take on faith. Everything here is an estimate (§2.7).
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { Lightbulb, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { trimRate } from "@/components/caps/HeadroomBar";
import {
  Badge,
  Button,
  Field,
  Input,
  ProvisionalChip,
  SELECT_CLASS,
} from "@/components/ui/primitives";
import {
  capsApi,
  plannerApi,
  type PlanCard,
  type PlanChannel,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatLast4, formatMoney } from "@/lib/format";

const CHANNELS: { value: PlanChannel; label: string }[] = [
  { value: "online", label: "Online" },
  { value: "offline", label: "In a shop" },
  { value: "unknown", label: "Not sure" },
];

const BASIS_LABEL: Record<PlanCard["basis"], string> = {
  rules: "Your rules",
  learned: "Learned rate",
  none: "No data",
};

const SELECT = cn(SELECT_CLASS, "w-full");

export function PlannerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState<PlanChannel>("unknown");
  const [categoryId, setCategoryId] = useState("");
  const [merchant, setMerchant] = useState("");

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: capsApi.categories,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Debounced so typing "12500" doesn't fire five plans.
  const [request, setRequest] = useState<null | {
    amount: string;
    channel: PlanChannel;
    category_id: string | null;
    merchant: string | null;
  }>(null);
  const validAmount = /^\d+(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) > 0;
  useEffect(() => {
    if (!validAmount) {
      setRequest(null);
      return;
    }
    const t = setTimeout(
      () =>
        setRequest({
          amount: amount.trim(),
          channel,
          category_id: categoryId || null,
          merchant: merchant.trim() || null,
        }),
      300,
    );
    return () => clearTimeout(t);
  }, [amount, channel, categoryId, merchant, validAmount]);

  const plan = useQuery({
    queryKey: ["plan", request],
    queryFn: () => plannerApi.plan(request!),
    enabled: open && request !== null,
    staleTime: 0,
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="fixed left-1/2 top-[6vh] z-50 max-h-[88vh] w-[min(42rem,94vw)] -translate-x-1/2 overflow-y-auto dialog-content">
          <div className="flex items-center justify-between border-b border-hairline px-5 py-4">
            <div>
              <Dialog.Title className="font-display text-lg font-semibold text-ink">
                Which card should this go on?
              </Dialog.Title>
              <Dialog.Description className="text-xs text-faint">
                Every card ranked by the cashback this purchase would earn. Estimates.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Amount (₹)">
                <Input
                  autoFocus
                  inputMode="decimal"
                  placeholder="e.g. 2500"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[,₹\s]/g, ""))}
                />
              </Field>
              <Field label="Where (optional)">
                <Input
                  placeholder="Merchant, e.g. Swiggy"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                />
              </Field>
              <Field label="Category (optional)">
                <select
                  className={SELECT}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">
                    {plan.data?.category_inferred && plan.data.category_name
                      ? `Auto: ${plan.data.category_name}`
                      : "Not set"}
                  </option>
                  {categories?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Online or in a shop?">
                <div className="flex gap-1 rounded-lg border border-hairline bg-base p-0.5">
                  {CHANNELS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setChannel(c.value)}
                      aria-pressed={channel === c.value}
                      className={cn(
                        "h-8 flex-1 rounded-md text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
                        channel === c.value ? "bg-raised text-ink" : "text-muted hover:text-ink",
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            {!validAmount ? (
              <p className="py-6 text-center text-sm text-faint">
                Type an amount to rank your cards.
              </p>
            ) : plan.isError ? (
              <p className="text-sm text-overdue">{(plan.error as Error).message}</p>
            ) : !plan.data ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-faint" aria-hidden />
              </div>
            ) : (
              <div className="space-y-2">
                <ol className="space-y-2">
                  {plan.data.results.map((r, i) => (
                    <PlanRow
                      key={r.card_id}
                      result={r}
                      best={i === 0 && r.basis !== "none" && Number(r.estimated_cashback) > 0}
                      onNavigate={() => onOpenChange(false)}
                    />
                  ))}
                </ol>
                <p className="pt-1 text-xs text-faint">{plan.data.caveat}</p>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PlanRow({
  result: r,
  best,
  onNavigate,
}: {
  result: PlanCard;
  best: boolean;
  onNavigate: () => void;
}) {
  const name = r.nickname || [r.issuer, r.product_name].filter(Boolean).join(" ");
  const from = r.theme?.gradient_from ?? "#1c2431";
  const to = r.theme?.gradient_to ?? "#2b3546";

  return (
    <li
      className={cn(
        "rounded-lg border p-3",
        best ? "border-brass/40 bg-brass/5" : "border-hairline",
        r.basis === "none" && "opacity-70",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="mt-0.5 h-6 w-9 shrink-0 rounded-[4px]"
          style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={`/cards/${r.card_id}`}
              onClick={onNavigate}
              className="truncate text-sm font-medium text-ink hover:underline"
            >
              {name}
            </Link>
            <span className="text-xs tabular-nums text-faint">{formatLast4(r.last4)}</span>
            {best && <Badge tone="brass">Best</Badge>}
            <span className="text-xs text-faint">
              {BASIS_LABEL[r.basis]}
              {r.confidence === "weak" && " · rough"}
            </span>
          </div>
          <ul className="mt-1.5 space-y-0.5 text-xs text-muted">
            {r.reasons.map((line) => (
              <li key={line}>{line}</li>
            ))}
            {r.hints.map((line) => (
              <li key={line} className="flex gap-1.5 text-brass">
                <Lightbulb className="mt-px h-3 w-3 shrink-0" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="shrink-0 text-right">
          {r.basis === "none" ? (
            <span className="text-sm text-faint">—</span>
          ) : (
            <>
              <div className="flex items-center justify-end gap-1.5">
                <ProvisionalChip kind="est" />
                <span className="font-display text-base font-semibold tabular-nums text-ink">
                  {formatMoney(r.estimated_cashback)}
                </span>
              </div>
              {r.effective_rate && (
                <div className="text-xs tabular-nums text-faint">
                  {trimRate(r.effective_rate)}% back
                </div>
              )}
            </>
          )}
          {r.float_days !== null && (
            <div className="text-xs tabular-nums text-faint" title="Interest-free days for a purchase today">
              {r.float_days}d to pay
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
